import GenericApi from "@/api/genericApi";
import { Loader } from "@/components";
import {
  BankAccountQueryKey,
  ClientInfoQueryKey,
  CryptoAccountQueryKey,
  GenericStatusQueryKey,
  UsersQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { useAppDispatch } from "@/store";
import { setActiveComponent } from "@/store/slices/componentSlice";
import { ApiError, ApiResponse } from "@/types/Api";
import { EditFee } from "@/types/Fee";
import { ClientInfo, User } from "@/types/User";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import ClientSettings from "./ClientSettings";
import { selectClient } from "@/store/slices/clientSlice.tsx";
import { setClientTransfer } from "@/store/slices/clientTransactionSlice";
import { BankAccount, CryptoAccount } from "@/types/BankSettings.tsx";
import { GeneralStatus } from "@/types/BusinessInfoNEW";
import { getUser } from "@/utils/localStorage";
import { checkPermissionByKeyAndValue, isBitramp } from "@/utils/utils";

type ClientStatus =
  | "approved"
  | "pending"
  | "frozen"
  | "rejected"
  | "not_initiated";

const Clients: React.FC = () => {
  const [declineReasonError, setDeclineReasonError] = useState(false);

  const [clientSettings, setClientSettings] = useState<number | null>(null);
  const [selectClientId, setSelectClientId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [confirtStatusType, setConfirmStatusType] = useState<string>("");
  const [clinetIdForConfirm, setClientIdForConfirm] = useState<number>(0);
  const [selectedClient, setSelectedClient] = useState<number | null>(null);

  const [declineReason, setDeclineReason] = useState<string>("");

  const user = getUser();

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(
      d.getMonth() + 1
    )}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
      d.getSeconds()
    )}`;
  };

  const canEditClientsNEW =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "clientinfo"
    ) || user?.is_superuser;

  const canAccessClientsNEW =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "view",
      "clientinfo"
    ) || user?.is_superuser;

  const canAccessTransactionsNEW =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "view",
      "transaction"
    ) || user?.is_superuser;

  const { data: bankAccounts } = useQuery<ApiResponse<BankAccount[]>>({
    queryKey: [BankAccountQueryKey, selectedClient],
    queryFn: async () => {
      const url = selectedClient
        ? `${ApiRoutes.BANKACCOUNT}/user/${selectedClient}`
        : ApiRoutes.BANKACCOUNT;
      return GenericApi.get<BankAccount[]>(url);
    },
    enabled: !!selectedClient,
  });

  const { data: cryptoAccounts } = useQuery<ApiResponse<CryptoAccount[]>>({
    queryKey: [CryptoAccountQueryKey, selectedClient],
    queryFn: async () => {
      const url = selectedClient
        ? `${ApiRoutes.CRYPTOACCOUNT}/user/${selectedClient}`
        : ApiRoutes.CRYPTOACCOUNT;
      return GenericApi.get<CryptoAccount[]>(url);
    },
    enabled: !!selectedClient,
  });

  const clientHasWarning =
    bankAccounts?.data?.find((acc) => acc.status.status === "pending") ||
    cryptoAccounts?.data?.find((acc) => acc.status.status === "pending");

  const {
    data: users,
    isLoading: isLoadingUsers,
    error: errorUsers,
  } = useQuery<ApiResponse<User[]>>({
    queryFn: async () => GenericApi.get<User[]>(ApiRoutes.USERS),
    queryKey: [UsersQueryKey],
  });

  const { data: userGeneralStatus } = useQuery<ApiResponse<GeneralStatus>>({
    queryFn: async () =>
      GenericApi.get<GeneralStatus>(
        `${ApiRoutes.GENERALSTATUS}?user_id=${selectClientId}`
      ),
    queryKey: [GenericStatusQueryKey, selectedClient],
    enabled: !!selectedClient,
  });

  const { data: userClientInfo } = useQuery<ApiResponse<ClientInfo>>({
    queryFn: async () =>
      GenericApi.get<ClientInfo>(
        `${ApiRoutes.CLIENTINFO}?user_id=${selectClientId}`
      ),
    queryKey: [ClientInfoQueryKey, selectedClient],
    enabled: !!selectedClient,
  });

  useEffect(() => {
    if (userClientInfo?.data) {
      setDeclineReason(userClientInfo.data.decline_reason);
    }
  }, [userClientInfo]);

  const dispatch = useAppDispatch();

  const handleSettingsClick = (selected_client: number) => {
    setClientSettings(selected_client);
    const client = users?.data?.find((user) => user.id === selected_client);
    dispatch(selectClient(client ?? null));
  };

  const handleTransactionClick = (selected_client: number) => {
    dispatch(setActiveComponent("Transactions"));
    dispatch(setClientTransfer(selected_client));
    // const client = users?.data?.find((user) => user.id === selected_client);
    // dispatch(selectClient(client ?? null));
    handleSettingsClick(selected_client);
  };

  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [filters, setFilters] = useState<ClientStatus[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const rowRef = useRef<HTMLDivElement | null>(null);

  // const toggleRow = (index: number) => {
  //   setExpandedRow((prevIndex) => (prevIndex === index ? prevIndex : index));
  //   setSelectedClient(index);
  // };

  const toggleRow = (index: number) => {
    setExpandedRow((prevIndex) => (prevIndex === index ? null : index));
    setSelectedClient(index); // Poți păstra această linie dacă vrei
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(event.target as Node)) {
        setExpandedRow(null); // Închide rândul când apeși în afară
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleFilter = (status: ClientStatus) => {
    setFilters((prevFilters) =>
      prevFilters.includes(status)
        ? prevFilters.filter((filter) => filter !== status)
        : [...prevFilters, status]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsersByRole = () => {
    return users?.data?.filter((user) => !user.is_staff && !user.is_superuser);
  };

  const clients = filteredUsersByRole();

  const renderClientsByStatus = () => {
    let filteredClients = clients;
    const search = searchTerm.toLowerCase();

    if (search.length > 0) {
      filteredClients = filteredClients?.filter(
        (client) =>
          client.first_name.toLowerCase().includes(search) ||
          client.last_name.toLowerCase().includes(search) ||
          client.email.toLowerCase().includes(search)
      );
    }

    if (filters.length > 0) {
      filteredClients = filteredClients?.filter((client) => {
        const status = client?.client_info?.status;

        if (status === null || status === undefined) {
          return filters.includes("not_initiated");
        }

        return filters.includes(status as ClientStatus);
      });
    }

    return filteredClients;
  };

  const [fee, setFee] = useState<EditFee>({
    txId: "-1",
    valueFee: 0,
  });

  const queryClient = useQueryClient();

  const updateClientStatusMutation = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    { clientId: number; status: ClientStatus; decline_reason?: string }
  >({
    mutationFn: ({ clientId, status }) =>
      GenericApi.put<ClientInfo>(
        `${ApiRoutes.CLIENTINFO}?user_id=${clientId}`,
        {
          status,
          decline_reason: declineReason,
        }
      ),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [UsersQueryKey, ClientInfoQueryKey],
      });
      CustomLogger.log("Status updated successfully");
    },
    onError: async (error) => {
      CustomLogger.error(error);
    },
  });

  const handleApprove = (clientId: number) => {
    updateClientStatusMutation.mutate({ clientId, status: "approved" });
  };

  const handleFreeze = (clientId: number) => {
    updateClientStatusMutation.mutate({ clientId, status: "frozen" });
  };

  const handleDecline = (clientId: number) => {
    updateClientStatusMutation.mutate({
      clientId,
      status: "rejected",
      decline_reason: declineReason ?? undefined,
    });
  };

  const handlePending = (clientId: number) => {
    updateClientStatusMutation.mutate({ clientId, status: "pending" });
  };

  const handleSave = () => {
    if (fee.txId === "-1") {
      return; // TODO: alert user
    }

    const user = users?.data?.find((user) => user.id === Number(fee.txId));
    if (!user || user.client_info === null) {
      return;
    }

    const payload: ClientInfo = {
      kyc_status: user.client_info.kyc_status,
      admin_notes: user.client_info.admin_notes,
      decline_reason: user.client_info.decline_reason,
      status: user.client_info.status,
      fee: fee.valueFee,
      seller: user.client_info.seller,
      user_id: Number(fee.txId),
    };
    editClientFeeMutation.mutate(payload);

    // editClientFeeMutation.mutate({
    //   ...user.client_info,
    //   fee: fee.valueFee,
    // });
  };

  const editClientFeeMutation = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    ClientInfo
  >({
    mutationFn: (data) =>
      GenericApi.put(`${ApiRoutes.USERSFEE}?user_id=${data.user_id}`, {
        kyc_status: data.kyc_status,
        admin_notes: data.admin_notes,
        decline_reason: data.decline_reason,
        status: data.status,
        fee: data.fee,
        seller: data.seller,
      }),
    onSuccess: ({ data }) => {
      setFee({ txId: "-1", valueFee: 0 });
      queryClient.invalidateQueries({ queryKey: [UsersQueryKey] });
      CustomLogger.log(data);
    },
    onError: ({ error }) => {
      CustomLogger.error(error);
    },
  });

  if (isLoadingUsers) {
    return <Loader />;
  }

  if (errorUsers) {
    return <div>Error: {errorUsers?.message}</div>;
  }

  const filteredClients = renderClientsByStatus();

  return (
    <div className="min-h-screen">
      {!clientSettings ? (
        <div>
          <div className="flex justify-between items-center ps-8 pe-4">
            <h2 className="Heading2 bold dark-blue">Clients</h2>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className={`p-2 border-[#ffffff] placeholder-[#73779130] rounded-[0.625rem] text-[#000] font-satoshi text-[1.1rem] font-medium w-[30%] Input pshadow ${
                isBitramp() ? "!bg-[#464646] placeholder-[#9F9F9F]" : ""
              }`}
            />
            <div className="flex gap-4 p-4">
              <button
                onClick={() => toggleFilter("approved")}
                className={`px-4 py-2 border rounded-lg ${
                  filters.includes("approved")
                    ? isBitramp()
                      ? " text-[#2DDE24] border border-[#2DDE24] bg-[#2cde233e]"
                      : "Approve_button_active"
                    : isBitramp()
                    ? "text-[#2DDE24] border border-[#2dde248d] hover:border-[#2DDE24]"
                    : "Approve_button"
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => toggleFilter("pending")}
                className={`px-4 py-2 border rounded-lg ${
                  filters.includes("pending")
                    ? isBitramp()
                      ? "border-[#f00] text-[#f00] bg-[#ff00002d]"
                      : "Outline_red_button_active"
                    : isBitramp()
                    ? "hover:border-[#F00] border-[#ff000094] text-[#f00]"
                    : "Outline_red_button"
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => toggleFilter("frozen")}
                className={`px-4 py-2 border rounded-lg ${
                  filters.includes("frozen")
                    ? isBitramp()
                      ? "border-[#07baeb] text-[#07baeb] bg-[#07baeb21]"
                      : "Freeze_button_active"
                    : isBitramp()
                    ? "hover:border-[#07BAEB] text-[#07baeb] border-[#07baeb9e]"
                    : "Freeze_button"
                }`}
              >
                Frozen
              </button>
              <button
                onClick={() => toggleFilter("not_initiated")}
                className={`px-4 py-2 border rounded-lg ${
                  filters.includes("not_initiated")
                    ? isBitramp()
                      ? "border-[#c3c3c3] text-[#c3c3c3] bg-[#c3c3c328]"
                      : "Pending_button_active"
                    : isBitramp()
                    ? "hover:border-[#c3c3c3] text-[#c3c3c3] border-[#c3c3c383]"
                    : "Pending_button"
                }`}
              >
                Not initiated
              </button>
            </div>
          </div>

          <div
            className={`w-[100%] mx-auto shadow rounded-3xl ${
              isBitramp() ? "bg-[#262626]" : "bg-white "
            }`}
          >
            <div className="py-6">
              {filteredClients?.map((client) => (
                <div
                  key={client.id}
                  ref={expandedRow === client.id ? rowRef : null}
                >
                  <div
                    className="flex items-start px-8 py-1 cursor-pointer"
                    onClick={() => {
                      toggleRow(client.id);
                      setSelectClientId(client.id);
                    }}
                  >
                    <div
                      className={`border p-2 pl-8 rounded-lg w-[100%] flex flex-wrap justify-between ${
                        expandedRow === client.id
                          ? isBitramp()
                            ? "!bg-[#2B2B2B] !border-none"
                            : "bg-[#F9F9F9]"
                          : isBitramp()
                          ? "!bg-[#2B2B2B] !border-none"
                          : ""
                      }`}
                    >
                      <span
                        className={`w-1/6 font-semibold  ${
                          expandedRow === client.id ? "mb-4" : ""
                        }
                          ${isBitramp() ? "text-[#ffffff]" : ""}`}
                      >
                        {/* {client.first_name} {client.last_name} */}
                        {client.business_name?.trim()
                          ? client.business_name
                          : `${client.first_name} ${client.last_name}`}
                      </span>
                      <span
                        className={`w-3/12  ${
                          client?.client_info?.status === "approved"
                            ? "text-[#2DDE24]"
                            : client?.client_info?.status === "frozen"
                            ? "text-[#07BAEB]"
                            : client?.client_info?.status === "rejected"
                            ? "text-[#FF3838]"
                            : client?.client_info?.status === "pending"
                            ? "text-black"
                            : "text-slate-300"
                        }`}
                      >
                        {client?.client_info !== null
                          ? client?.client_info?.status
                          : "not initiated"}
                      </span>
                      <span
                        className={`w-2/12 ${
                          isBitramp() ? "text-[#ffffff]" : ""
                        }`}
                      >
                        {formatDate(client?.date_joined)}
                      </span>
                      <button
                        className={`w-1/12 flex justify-end items-center mr-4 text-gray-600  ${
                          expandedRow === client.id ? "mb-3" : ""
                        }`}
                      >
                        {expandedRow === client.id ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M0.153898 13.0533L6.90421 6.303L13.6498 13.0486L13.6509 6.75127L6.89949 -8.23736e-05L0.15782 6.74159L0.153898 13.0533Z"
                              fill={`${isBitramp() ? "#EE33D9" : "#202A43"}`}
                              fill-opacity={`${isBitramp() ? "" : "0.3"}`}
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M13.6449 0.745517L6.89462 7.49583L0.149023 0.750229L0.147976 7.04755L6.89933 13.7989L13.641 7.05723L13.6449 0.745517Z"
                              fill={`${isBitramp() ? "#EE33D9" : "#202A43"}`}
                            />
                          </svg>
                        )}
                      </button>
                      {expandedRow === client.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`p-4  w-full border-t ${
                            isBitramp()
                              ? "bg-[#2B2B2B] border-t-[#464646]"
                              : "bg-gray-50"
                          }`}
                        >
                          <div className="flex w-full justify-between">
                            <ul className="text-[#D0D0D0] space-y-2">
                              {/* <li>
                                {userGeneralStatus?.data?.business_info
                                  .status == "approved"
                                  ? "✓"
                                  : "✘"}{" "}
                                <span className="text-[#000]">
                                  Business Info
                                </span>{" "}
                                last change{" "}
                                {formatDate(
                                  userGeneralStatus?.data?.business_info
                                    ?.last_updated
                                    ? userGeneralStatus?.data?.business_info
                                        ?.last_updated
                                    : ""
                                )}
                              </li> */}

                              <li>
                                <div
                                  className={`flex items-center gap-1  ${
                                    isBitramp()
                                      ? "text-[#ffffff]"
                                      : "text-[#000]"
                                  }`}
                                >
                                  {userGeneralStatus?.data?.business_info
                                    .status === "approved" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="15"
                                      height="12"
                                      viewBox="0 0 15 12"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_3970_7677)">
                                        <path
                                          d="M5.40013 8.87332C5.55989 8.69304 5.71404 8.51923 5.86797 8.34541C6.36071 7.78853 6.85346 7.23165 7.34598 6.67453C7.85398 6.10017 8.36154 5.52533 8.86999 4.95121C9.1471 4.6383 9.42668 4.32777 9.70357 4.01438C10.0698 3.59971 10.4337 3.18313 10.7992 2.76822C11.1167 2.4079 11.4354 2.04877 11.7535 1.68917C12.1256 1.26852 12.4983 0.848582 12.8692 0.426732C13.0484 0.222989 13.2621 0.084367 13.519 0.0283437C14.2298 -0.126079 14.8576 0.369272 14.9766 1.05759C15.0531 1.49979 14.9431 1.88693 14.6548 2.21421C14.1428 2.79551 13.6285 3.37466 13.1153 3.955C12.1352 5.06326 11.1551 6.17151 10.1748 7.27953C9.65129 7.87137 9.12713 8.46224 8.60365 9.05432C7.92893 9.81734 7.25488 10.5811 6.58016 11.3443C6.49557 11.4401 6.41165 11.5368 6.32324 11.6285C5.85496 12.1129 5.13312 12.1248 4.64688 11.6592C4.17949 11.2115 3.71098 10.7654 3.24336 10.3182C2.55294 9.65813 1.86296 8.99782 1.17276 8.33799C0.911126 8.0878 0.645232 7.8424 0.388089 7.58694C-0.163444 7.03964 -0.120362 6.09466 0.474252 5.60123C0.933564 5.22032 1.58248 5.23588 2.02451 5.64743C2.38218 5.98046 2.73222 6.32282 3.08607 6.66064C3.5373 7.09159 3.98921 7.52182 4.44 7.95325C4.73506 8.23552 5.02855 8.5197 5.32272 8.80269C5.34471 8.824 5.3676 8.84411 5.39968 8.87356L5.40013 8.87332Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#222B45"
                                          }`}
                                          fill-opacity={`${
                                            isBitramp() ? "" : "0.3"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_3970_7677">
                                          <rect
                                            width="15"
                                            height="12"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : userGeneralStatus?.data?.business_info
                                      .status === "pending" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      height="16"
                                      viewBox="0 0 10 16"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_4829_19950)">
                                        <path
                                          d="M8.10147 9.5741C8.02991 9.49969 7.92619 9.48252 7.87875 9.43428C7.83131 9.38603 7.81362 9.28218 7.74126 9.20777C7.48155 8.94038 6.85439 8.41215 6.68875 8.14885C6.66463 8.1096 6.63408 8.08343 6.65498 8.0311C6.95168 7.7817 7.16636 7.43744 7.41401 7.16351C8.04036 6.47174 8.83557 5.8282 9.42816 5.12416C9.675 4.83061 9.913 4.50025 9.9516 4.10203C10.0248 3.33912 10.0071 1.93021 9.9524 1.15258C9.92345 0.743725 9.83662 0.379847 9.42976 0.2114C9.25046 0.136989 8.68119 0.0633957 8.4641 0.048677C6.0841 -0.10914 3.54008 0.174603 1.14159 0.0494947C0.510413 0.11164 0.120447 0.401925 0.0561231 1.07162C-0.0162416 1.82228 -0.0170456 3.43315 0.0561231 4.1838C0.120447 4.85023 0.806304 5.31142 1.22682 5.77342C1.32009 5.87563 1.36994 6.00401 1.46402 6.10541C2.07349 6.76284 2.74246 7.36794 3.35515 8.02456C2.73442 8.67381 2.08475 9.29527 1.46402 9.94371C1.24853 10.1686 1.03868 10.3812 0.822385 10.5995C0.480663 10.9438 0.106778 11.2602 0.0561231 11.7835C-0.022674 12.5931 -0.0146335 14.2424 0.0561231 15.0593C0.109995 15.6742 0.379352 15.9456 0.980783 15.9996H9.02693C9.6541 15.9383 9.88647 15.4738 9.94436 14.8884C10.0071 14.2465 10.0328 12.4876 9.93149 11.8849C9.80928 11.158 8.62732 10.1187 8.10147 9.5741ZM2.49964 4.43238H7.46627L5.4682 6.29266C5.20286 6.53961 4.79601 6.54042 4.52907 6.29593L2.49964 4.43238ZM5.49232 9.745C5.49232 10.0214 5.27201 10.2454 5.00024 10.2454C4.72847 10.2454 4.50816 10.0214 4.50816 9.745C4.50816 9.46862 4.72847 9.24375 5.00024 9.24375C5.27201 9.24375 5.49232 9.4678 5.49232 9.745ZM1.70604 14.0012L4.06915 12.266C4.62153 11.8612 5.3701 11.8735 5.90882 12.2971L8.07815 14.0012H1.70604Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#B8BBC2"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_4829_19950">
                                          <rect
                                            width="10"
                                            height="16"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="11"
                                      height="11"
                                      viewBox="0 0 11 11"
                                      fill="none"
                                    >
                                      <path
                                        d="M10.6469 8.89019C10.888 9.13136 11.0114 9.42225 10.9942 9.76115C10.9657 10.3115 10.6646 10.6847 10.1767 10.895C9.70308 11.0996 9.26204 11.0007 8.89641 10.6378C7.92349 9.67256 6.95742 8.70046 5.98792 7.73121C5.88052 7.62377 5.77769 7.51518 5.60972 7.4969C5.38806 7.47289 5.20353 7.5209 5.04242 7.68835C4.68536 8.05753 4.31744 8.417 3.9541 8.78047C3.34167 9.3931 2.7298 10.0069 2.11623 10.6184C1.88657 10.847 1.6192 10.983 1.2827 10.9876C0.927354 10.9927 0.627993 10.8596 0.388047 10.623C0.153814 10.3921 -0.0112915 10.1652 0.00641879 9.75257C0.0012771 9.41996 0.10811 9.14108 0.332631 8.91648C1.1593 8.08782 1.9894 7.26201 2.82064 6.43792C2.97546 6.28476 3.14057 6.14189 3.30167 5.99445C3.64731 5.67841 3.61646 5.29551 3.2931 4.98233C2.33046 4.04909 1.37754 3.10499 0.423467 2.1626C0.329774 2.07001 0.236652 1.97 0.171524 1.85685C-0.200392 1.20935 0.0464098 0.452694 0.777102 0.106941C1.0976 -0.044504 1.39411 -0.0227873 1.69232 0.163519C2.07224 0.400116 2.42244 0.671003 2.7378 0.989324C3.5022 1.75855 4.2706 2.52321 5.03671 3.29015C5.1544 3.40788 5.2818 3.49246 5.45833 3.49931C5.652 3.50674 5.80568 3.44731 5.94165 3.30615C6.22558 3.01126 6.51866 2.72551 6.80831 2.43634C7.49501 1.74941 8.18286 1.06476 8.8667 0.374971C9.13007 0.109227 9.44257 -0.0193584 9.81392 0.00235831C9.91618 0.00864471 10.0219 0.026361 10.1178 0.0617934C10.7611 0.300105 11.2844 1.08648 10.8268 1.86599C10.7697 1.96257 10.692 2.04887 10.6126 2.12888C9.65224 3.09356 8.69246 4.05938 7.72524 5.01662C7.50929 5.23036 7.4396 5.45896 7.56128 5.73327C7.60184 5.82471 7.67326 5.90701 7.74467 5.97902C8.71074 6.95112 9.67852 7.92094 10.6469 8.89019Z"
                                        fill={`${
                                          isBitramp() ? "#EE33D9" : "#222B45"
                                        }`}
                                        fill-opacity={`${
                                          isBitramp() ? "" : "0.3"
                                        }`}
                                      />
                                    </svg>
                                  )}
                                  <span>Business Info</span>
                                  <span className="ml-1 text-sm text-gray-500">
                                    last change{" "}
                                    {formatDate(
                                      userGeneralStatus?.data?.business_info
                                        ?.last_updated || ""
                                    )}
                                  </span>
                                </div>
                              </li>

                              {/* <li>
                                {userGeneralStatus?.data?.individual_info
                                  .status == "approved"
                                  ? "✓"
                                  : "✘"}{" "}
                                <span className="text-[#000]">Individual</span>{" "}
                                last change{" "}
                                {formatDate(
                                  userGeneralStatus?.data?.individual_info
                                    ?.last_updated
                                    ? userGeneralStatus?.data?.individual_info
                                        ?.last_updated
                                    : ""
                                )}
                              </li> */}

                              <li>
                                <div
                                  className={`flex items-center gap-1  ${
                                    isBitramp()
                                      ? "text-[#ffffff]"
                                      : "text-[#000]"
                                  }`}
                                >
                                  {userGeneralStatus?.data?.individual_info
                                    .status === "approved" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="15"
                                      height="12"
                                      viewBox="0 0 15 12"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_3970_7677)">
                                        <path
                                          d="M5.40013 8.87332C5.55989 8.69304 5.71404 8.51923 5.86797 8.34541C6.36071 7.78853 6.85346 7.23165 7.34598 6.67453C7.85398 6.10017 8.36154 5.52533 8.86999 4.95121C9.1471 4.6383 9.42668 4.32777 9.70357 4.01438C10.0698 3.59971 10.4337 3.18313 10.7992 2.76822C11.1167 2.4079 11.4354 2.04877 11.7535 1.68917C12.1256 1.26852 12.4983 0.848582 12.8692 0.426732C13.0484 0.222989 13.2621 0.084367 13.519 0.0283437C14.2298 -0.126079 14.8576 0.369272 14.9766 1.05759C15.0531 1.49979 14.9431 1.88693 14.6548 2.21421C14.1428 2.79551 13.6285 3.37466 13.1153 3.955C12.1352 5.06326 11.1551 6.17151 10.1748 7.27953C9.65129 7.87137 9.12713 8.46224 8.60365 9.05432C7.92893 9.81734 7.25488 10.5811 6.58016 11.3443C6.49557 11.4401 6.41165 11.5368 6.32324 11.6285C5.85496 12.1129 5.13312 12.1248 4.64688 11.6592C4.17949 11.2115 3.71098 10.7654 3.24336 10.3182C2.55294 9.65813 1.86296 8.99782 1.17276 8.33799C0.911126 8.0878 0.645232 7.8424 0.388089 7.58694C-0.163444 7.03964 -0.120362 6.09466 0.474252 5.60123C0.933564 5.22032 1.58248 5.23588 2.02451 5.64743C2.38218 5.98046 2.73222 6.32282 3.08607 6.66064C3.5373 7.09159 3.98921 7.52182 4.44 7.95325C4.73506 8.23552 5.02855 8.5197 5.32272 8.80269C5.34471 8.824 5.3676 8.84411 5.39968 8.87356L5.40013 8.87332Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#222B45"
                                          }`}
                                          fill-opacity={`${
                                            isBitramp() ? "" : "0.3"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_3970_7677">
                                          <rect
                                            width="15"
                                            height="12"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : userGeneralStatus?.data?.individual_info
                                      .status === "pending" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      height="16"
                                      viewBox="0 0 10 16"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_4829_19950)">
                                        <path
                                          d="M8.10147 9.5741C8.02991 9.49969 7.92619 9.48252 7.87875 9.43428C7.83131 9.38603 7.81362 9.28218 7.74126 9.20777C7.48155 8.94038 6.85439 8.41215 6.68875 8.14885C6.66463 8.1096 6.63408 8.08343 6.65498 8.0311C6.95168 7.7817 7.16636 7.43744 7.41401 7.16351C8.04036 6.47174 8.83557 5.8282 9.42816 5.12416C9.675 4.83061 9.913 4.50025 9.9516 4.10203C10.0248 3.33912 10.0071 1.93021 9.9524 1.15258C9.92345 0.743725 9.83662 0.379847 9.42976 0.2114C9.25046 0.136989 8.68119 0.0633957 8.4641 0.048677C6.0841 -0.10914 3.54008 0.174603 1.14159 0.0494947C0.510413 0.11164 0.120447 0.401925 0.0561231 1.07162C-0.0162416 1.82228 -0.0170456 3.43315 0.0561231 4.1838C0.120447 4.85023 0.806304 5.31142 1.22682 5.77342C1.32009 5.87563 1.36994 6.00401 1.46402 6.10541C2.07349 6.76284 2.74246 7.36794 3.35515 8.02456C2.73442 8.67381 2.08475 9.29527 1.46402 9.94371C1.24853 10.1686 1.03868 10.3812 0.822385 10.5995C0.480663 10.9438 0.106778 11.2602 0.0561231 11.7835C-0.022674 12.5931 -0.0146335 14.2424 0.0561231 15.0593C0.109995 15.6742 0.379352 15.9456 0.980783 15.9996H9.02693C9.6541 15.9383 9.88647 15.4738 9.94436 14.8884C10.0071 14.2465 10.0328 12.4876 9.93149 11.8849C9.80928 11.158 8.62732 10.1187 8.10147 9.5741ZM2.49964 4.43238H7.46627L5.4682 6.29266C5.20286 6.53961 4.79601 6.54042 4.52907 6.29593L2.49964 4.43238ZM5.49232 9.745C5.49232 10.0214 5.27201 10.2454 5.00024 10.2454C4.72847 10.2454 4.50816 10.0214 4.50816 9.745C4.50816 9.46862 4.72847 9.24375 5.00024 9.24375C5.27201 9.24375 5.49232 9.4678 5.49232 9.745ZM1.70604 14.0012L4.06915 12.266C4.62153 11.8612 5.3701 11.8735 5.90882 12.2971L8.07815 14.0012H1.70604Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#B8BBC2"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_4829_19950">
                                          <rect
                                            width="10"
                                            height="16"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="11"
                                      height="11"
                                      viewBox="0 0 11 11"
                                      fill="none"
                                    >
                                      <path
                                        d="M10.6469 8.89019C10.888 9.13136 11.0114 9.42225 10.9942 9.76115C10.9657 10.3115 10.6646 10.6847 10.1767 10.895C9.70308 11.0996 9.26204 11.0007 8.89641 10.6378C7.92349 9.67256 6.95742 8.70046 5.98792 7.73121C5.88052 7.62377 5.77769 7.51518 5.60972 7.4969C5.38806 7.47289 5.20353 7.5209 5.04242 7.68835C4.68536 8.05753 4.31744 8.417 3.9541 8.78047C3.34167 9.3931 2.7298 10.0069 2.11623 10.6184C1.88657 10.847 1.6192 10.983 1.2827 10.9876C0.927354 10.9927 0.627993 10.8596 0.388047 10.623C0.153814 10.3921 -0.0112915 10.1652 0.00641879 9.75257C0.0012771 9.41996 0.10811 9.14108 0.332631 8.91648C1.1593 8.08782 1.9894 7.26201 2.82064 6.43792C2.97546 6.28476 3.14057 6.14189 3.30167 5.99445C3.64731 5.67841 3.61646 5.29551 3.2931 4.98233C2.33046 4.04909 1.37754 3.10499 0.423467 2.1626C0.329774 2.07001 0.236652 1.97 0.171524 1.85685C-0.200392 1.20935 0.0464098 0.452694 0.777102 0.106941C1.0976 -0.044504 1.39411 -0.0227873 1.69232 0.163519C2.07224 0.400116 2.42244 0.671003 2.7378 0.989324C3.5022 1.75855 4.2706 2.52321 5.03671 3.29015C5.1544 3.40788 5.2818 3.49246 5.45833 3.49931C5.652 3.50674 5.80568 3.44731 5.94165 3.30615C6.22558 3.01126 6.51866 2.72551 6.80831 2.43634C7.49501 1.74941 8.18286 1.06476 8.8667 0.374971C9.13007 0.109227 9.44257 -0.0193584 9.81392 0.00235831C9.91618 0.00864471 10.0219 0.026361 10.1178 0.0617934C10.7611 0.300105 11.2844 1.08648 10.8268 1.86599C10.7697 1.96257 10.692 2.04887 10.6126 2.12888C9.65224 3.09356 8.69246 4.05938 7.72524 5.01662C7.50929 5.23036 7.4396 5.45896 7.56128 5.73327C7.60184 5.82471 7.67326 5.90701 7.74467 5.97902C8.71074 6.95112 9.67852 7.92094 10.6469 8.89019Z"
                                        fill={`${
                                          isBitramp() ? "#EE33D9" : "#202A43"
                                        }`}
                                        fill-opacity={`${
                                          isBitramp() ? "" : "0.3"
                                        }`}
                                      />
                                    </svg>
                                  )}
                                  <span>Individual</span>
                                  <span className="ml-1 text-sm text-gray-500">
                                    last change{" "}
                                    {formatDate(
                                      userGeneralStatus?.data?.individual_info
                                        ?.last_updated || ""
                                    )}
                                  </span>
                                </div>
                              </li>

                              {/* <li>
                                {userGeneralStatus?.data?.financial_info
                                  .status == "approved"
                                  ? "✓"
                                  : "✘"}{" "}
                                <span className="text-[#000]">Questionary</span>{" "}
                                last change{" "}
                                {formatDate(
                                  userGeneralStatus?.data?.financial_info
                                    ?.last_updated
                                    ? userGeneralStatus?.data?.financial_info
                                        ?.last_updated
                                    : ""
                                )}
                              </li> */}

                              <li>
                                <div
                                  className={`flex items-center gap-1  ${
                                    isBitramp()
                                      ? "text-[#ffffff]"
                                      : "text-[#000]"
                                  }`}
                                >
                                  {userGeneralStatus?.data?.financial_info
                                    .status === "approved" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="15"
                                      height="12"
                                      viewBox="0 0 15 12"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_3970_7677)">
                                        <path
                                          d="M5.40013 8.87332C5.55989 8.69304 5.71404 8.51923 5.86797 8.34541C6.36071 7.78853 6.85346 7.23165 7.34598 6.67453C7.85398 6.10017 8.36154 5.52533 8.86999 4.95121C9.1471 4.6383 9.42668 4.32777 9.70357 4.01438C10.0698 3.59971 10.4337 3.18313 10.7992 2.76822C11.1167 2.4079 11.4354 2.04877 11.7535 1.68917C12.1256 1.26852 12.4983 0.848582 12.8692 0.426732C13.0484 0.222989 13.2621 0.084367 13.519 0.0283437C14.2298 -0.126079 14.8576 0.369272 14.9766 1.05759C15.0531 1.49979 14.9431 1.88693 14.6548 2.21421C14.1428 2.79551 13.6285 3.37466 13.1153 3.955C12.1352 5.06326 11.1551 6.17151 10.1748 7.27953C9.65129 7.87137 9.12713 8.46224 8.60365 9.05432C7.92893 9.81734 7.25488 10.5811 6.58016 11.3443C6.49557 11.4401 6.41165 11.5368 6.32324 11.6285C5.85496 12.1129 5.13312 12.1248 4.64688 11.6592C4.17949 11.2115 3.71098 10.7654 3.24336 10.3182C2.55294 9.65813 1.86296 8.99782 1.17276 8.33799C0.911126 8.0878 0.645232 7.8424 0.388089 7.58694C-0.163444 7.03964 -0.120362 6.09466 0.474252 5.60123C0.933564 5.22032 1.58248 5.23588 2.02451 5.64743C2.38218 5.98046 2.73222 6.32282 3.08607 6.66064C3.5373 7.09159 3.98921 7.52182 4.44 7.95325C4.73506 8.23552 5.02855 8.5197 5.32272 8.80269C5.34471 8.824 5.3676 8.84411 5.39968 8.87356L5.40013 8.87332Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#202A43"
                                          }`}
                                          fill-opacity={`${
                                            isBitramp() ? "" : "0.3"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_3970_7677">
                                          <rect
                                            width="15"
                                            height="12"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : userGeneralStatus?.data?.financial_info
                                      .status === "pending" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      height="16"
                                      viewBox="0 0 10 16"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_4829_19950)">
                                        <path
                                          d="M8.10147 9.5741C8.02991 9.49969 7.92619 9.48252 7.87875 9.43428C7.83131 9.38603 7.81362 9.28218 7.74126 9.20777C7.48155 8.94038 6.85439 8.41215 6.68875 8.14885C6.66463 8.1096 6.63408 8.08343 6.65498 8.0311C6.95168 7.7817 7.16636 7.43744 7.41401 7.16351C8.04036 6.47174 8.83557 5.8282 9.42816 5.12416C9.675 4.83061 9.913 4.50025 9.9516 4.10203C10.0248 3.33912 10.0071 1.93021 9.9524 1.15258C9.92345 0.743725 9.83662 0.379847 9.42976 0.2114C9.25046 0.136989 8.68119 0.0633957 8.4641 0.048677C6.0841 -0.10914 3.54008 0.174603 1.14159 0.0494947C0.510413 0.11164 0.120447 0.401925 0.0561231 1.07162C-0.0162416 1.82228 -0.0170456 3.43315 0.0561231 4.1838C0.120447 4.85023 0.806304 5.31142 1.22682 5.77342C1.32009 5.87563 1.36994 6.00401 1.46402 6.10541C2.07349 6.76284 2.74246 7.36794 3.35515 8.02456C2.73442 8.67381 2.08475 9.29527 1.46402 9.94371C1.24853 10.1686 1.03868 10.3812 0.822385 10.5995C0.480663 10.9438 0.106778 11.2602 0.0561231 11.7835C-0.022674 12.5931 -0.0146335 14.2424 0.0561231 15.0593C0.109995 15.6742 0.379352 15.9456 0.980783 15.9996H9.02693C9.6541 15.9383 9.88647 15.4738 9.94436 14.8884C10.0071 14.2465 10.0328 12.4876 9.93149 11.8849C9.80928 11.158 8.62732 10.1187 8.10147 9.5741ZM2.49964 4.43238H7.46627L5.4682 6.29266C5.20286 6.53961 4.79601 6.54042 4.52907 6.29593L2.49964 4.43238ZM5.49232 9.745C5.49232 10.0214 5.27201 10.2454 5.00024 10.2454C4.72847 10.2454 4.50816 10.0214 4.50816 9.745C4.50816 9.46862 4.72847 9.24375 5.00024 9.24375C5.27201 9.24375 5.49232 9.4678 5.49232 9.745ZM1.70604 14.0012L4.06915 12.266C4.62153 11.8612 5.3701 11.8735 5.90882 12.2971L8.07815 14.0012H1.70604Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#B8BBC2"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_4829_19950">
                                          <rect
                                            width="10"
                                            height="16"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="11"
                                      height="11"
                                      viewBox="0 0 11 11"
                                      fill="none"
                                    >
                                      <path
                                        d="M10.6469 8.89019C10.888 9.13136 11.0114 9.42225 10.9942 9.76115C10.9657 10.3115 10.6646 10.6847 10.1767 10.895C9.70308 11.0996 9.26204 11.0007 8.89641 10.6378C7.92349 9.67256 6.95742 8.70046 5.98792 7.73121C5.88052 7.62377 5.77769 7.51518 5.60972 7.4969C5.38806 7.47289 5.20353 7.5209 5.04242 7.68835C4.68536 8.05753 4.31744 8.417 3.9541 8.78047C3.34167 9.3931 2.7298 10.0069 2.11623 10.6184C1.88657 10.847 1.6192 10.983 1.2827 10.9876C0.927354 10.9927 0.627993 10.8596 0.388047 10.623C0.153814 10.3921 -0.0112915 10.1652 0.00641879 9.75257C0.0012771 9.41996 0.10811 9.14108 0.332631 8.91648C1.1593 8.08782 1.9894 7.26201 2.82064 6.43792C2.97546 6.28476 3.14057 6.14189 3.30167 5.99445C3.64731 5.67841 3.61646 5.29551 3.2931 4.98233C2.33046 4.04909 1.37754 3.10499 0.423467 2.1626C0.329774 2.07001 0.236652 1.97 0.171524 1.85685C-0.200392 1.20935 0.0464098 0.452694 0.777102 0.106941C1.0976 -0.044504 1.39411 -0.0227873 1.69232 0.163519C2.07224 0.400116 2.42244 0.671003 2.7378 0.989324C3.5022 1.75855 4.2706 2.52321 5.03671 3.29015C5.1544 3.40788 5.2818 3.49246 5.45833 3.49931C5.652 3.50674 5.80568 3.44731 5.94165 3.30615C6.22558 3.01126 6.51866 2.72551 6.80831 2.43634C7.49501 1.74941 8.18286 1.06476 8.8667 0.374971C9.13007 0.109227 9.44257 -0.0193584 9.81392 0.00235831C9.91618 0.00864471 10.0219 0.026361 10.1178 0.0617934C10.7611 0.300105 11.2844 1.08648 10.8268 1.86599C10.7697 1.96257 10.692 2.04887 10.6126 2.12888C9.65224 3.09356 8.69246 4.05938 7.72524 5.01662C7.50929 5.23036 7.4396 5.45896 7.56128 5.73327C7.60184 5.82471 7.67326 5.90701 7.74467 5.97902C8.71074 6.95112 9.67852 7.92094 10.6469 8.89019Z"
                                        fill={`${
                                          isBitramp() ? "#EE33D9" : "#202A43"
                                        }`}
                                        fill-opacity={`${
                                          isBitramp() ? "" : "0.3"
                                        }`}
                                      />
                                    </svg>
                                  )}
                                  <span>Questionary</span>
                                  <span className="ml-1 text-sm text-gray-500">
                                    last change{" "}
                                    {formatDate(
                                      userGeneralStatus?.data?.financial_info
                                        ?.last_updated || ""
                                    )}
                                  </span>
                                </div>
                              </li>

                              {/* <li>
                                {userGeneralStatus?.data?.kyc_status.status ==
                                "approved"
                                  ? "✓"
                                  : "✘"}{" "}
                                <span className="text-[#000]">KYC</span> last
                                change{" "}
                                {formatDate(
                                  userGeneralStatus?.data?.kyc_status
                                    ?.last_updated
                                    ? userGeneralStatus?.data?.kyc_status
                                        ?.last_updated
                                    : ""
                                )}
                              </li> */}
                              <li>
                                <div
                                  className={`flex items-center gap-1  ${
                                    isBitramp()
                                      ? "text-[#ffffff]"
                                      : "text-[#000]"
                                  }`}
                                >
                                  {userGeneralStatus?.data?.kyc_status
                                    .status === "approved" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="15"
                                      height="12"
                                      viewBox="0 0 15 12"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_3970_7677)">
                                        <path
                                          d="M5.40013 8.87332C5.55989 8.69304 5.71404 8.51923 5.86797 8.34541C6.36071 7.78853 6.85346 7.23165 7.34598 6.67453C7.85398 6.10017 8.36154 5.52533 8.86999 4.95121C9.1471 4.6383 9.42668 4.32777 9.70357 4.01438C10.0698 3.59971 10.4337 3.18313 10.7992 2.76822C11.1167 2.4079 11.4354 2.04877 11.7535 1.68917C12.1256 1.26852 12.4983 0.848582 12.8692 0.426732C13.0484 0.222989 13.2621 0.084367 13.519 0.0283437C14.2298 -0.126079 14.8576 0.369272 14.9766 1.05759C15.0531 1.49979 14.9431 1.88693 14.6548 2.21421C14.1428 2.79551 13.6285 3.37466 13.1153 3.955C12.1352 5.06326 11.1551 6.17151 10.1748 7.27953C9.65129 7.87137 9.12713 8.46224 8.60365 9.05432C7.92893 9.81734 7.25488 10.5811 6.58016 11.3443C6.49557 11.4401 6.41165 11.5368 6.32324 11.6285C5.85496 12.1129 5.13312 12.1248 4.64688 11.6592C4.17949 11.2115 3.71098 10.7654 3.24336 10.3182C2.55294 9.65813 1.86296 8.99782 1.17276 8.33799C0.911126 8.0878 0.645232 7.8424 0.388089 7.58694C-0.163444 7.03964 -0.120362 6.09466 0.474252 5.60123C0.933564 5.22032 1.58248 5.23588 2.02451 5.64743C2.38218 5.98046 2.73222 6.32282 3.08607 6.66064C3.5373 7.09159 3.98921 7.52182 4.44 7.95325C4.73506 8.23552 5.02855 8.5197 5.32272 8.80269C5.34471 8.824 5.3676 8.84411 5.39968 8.87356L5.40013 8.87332Z"
                                          fill="#222B45"
                                          fill-opacity={`${
                                            isBitramp() ? "" : "0.3"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_3970_7677">
                                          <rect
                                            width="15"
                                            height="12"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : userGeneralStatus?.data?.kyc_status
                                      .status === "pending" ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      height="16"
                                      viewBox="0 0 10 16"
                                      fill="none"
                                    >
                                      <g clip-path="url(#clip0_4829_19950)">
                                        <path
                                          d="M8.10147 9.5741C8.02991 9.49969 7.92619 9.48252 7.87875 9.43428C7.83131 9.38603 7.81362 9.28218 7.74126 9.20777C7.48155 8.94038 6.85439 8.41215 6.68875 8.14885C6.66463 8.1096 6.63408 8.08343 6.65498 8.0311C6.95168 7.7817 7.16636 7.43744 7.41401 7.16351C8.04036 6.47174 8.83557 5.8282 9.42816 5.12416C9.675 4.83061 9.913 4.50025 9.9516 4.10203C10.0248 3.33912 10.0071 1.93021 9.9524 1.15258C9.92345 0.743725 9.83662 0.379847 9.42976 0.2114C9.25046 0.136989 8.68119 0.0633957 8.4641 0.048677C6.0841 -0.10914 3.54008 0.174603 1.14159 0.0494947C0.510413 0.11164 0.120447 0.401925 0.0561231 1.07162C-0.0162416 1.82228 -0.0170456 3.43315 0.0561231 4.1838C0.120447 4.85023 0.806304 5.31142 1.22682 5.77342C1.32009 5.87563 1.36994 6.00401 1.46402 6.10541C2.07349 6.76284 2.74246 7.36794 3.35515 8.02456C2.73442 8.67381 2.08475 9.29527 1.46402 9.94371C1.24853 10.1686 1.03868 10.3812 0.822385 10.5995C0.480663 10.9438 0.106778 11.2602 0.0561231 11.7835C-0.022674 12.5931 -0.0146335 14.2424 0.0561231 15.0593C0.109995 15.6742 0.379352 15.9456 0.980783 15.9996H9.02693C9.6541 15.9383 9.88647 15.4738 9.94436 14.8884C10.0071 14.2465 10.0328 12.4876 9.93149 11.8849C9.80928 11.158 8.62732 10.1187 8.10147 9.5741ZM2.49964 4.43238H7.46627L5.4682 6.29266C5.20286 6.53961 4.79601 6.54042 4.52907 6.29593L2.49964 4.43238ZM5.49232 9.745C5.49232 10.0214 5.27201 10.2454 5.00024 10.2454C4.72847 10.2454 4.50816 10.0214 4.50816 9.745C4.50816 9.46862 4.72847 9.24375 5.00024 9.24375C5.27201 9.24375 5.49232 9.4678 5.49232 9.745ZM1.70604 14.0012L4.06915 12.266C4.62153 11.8612 5.3701 11.8735 5.90882 12.2971L8.07815 14.0012H1.70604Z"
                                          fill={`${
                                            isBitramp() ? "#EE33D9" : "#B8BBC2"
                                          }`}
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_4829_19950">
                                          <rect
                                            width="10"
                                            height="16"
                                            fill="white"
                                          />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="11"
                                      height="11"
                                      viewBox="0 0 11 11"
                                      fill="none"
                                    >
                                      <path
                                        d="M10.6469 8.89019C10.888 9.13136 11.0114 9.42225 10.9942 9.76115C10.9657 10.3115 10.6646 10.6847 10.1767 10.895C9.70308 11.0996 9.26204 11.0007 8.89641 10.6378C7.92349 9.67256 6.95742 8.70046 5.98792 7.73121C5.88052 7.62377 5.77769 7.51518 5.60972 7.4969C5.38806 7.47289 5.20353 7.5209 5.04242 7.68835C4.68536 8.05753 4.31744 8.417 3.9541 8.78047C3.34167 9.3931 2.7298 10.0069 2.11623 10.6184C1.88657 10.847 1.6192 10.983 1.2827 10.9876C0.927354 10.9927 0.627993 10.8596 0.388047 10.623C0.153814 10.3921 -0.0112915 10.1652 0.00641879 9.75257C0.0012771 9.41996 0.10811 9.14108 0.332631 8.91648C1.1593 8.08782 1.9894 7.26201 2.82064 6.43792C2.97546 6.28476 3.14057 6.14189 3.30167 5.99445C3.64731 5.67841 3.61646 5.29551 3.2931 4.98233C2.33046 4.04909 1.37754 3.10499 0.423467 2.1626C0.329774 2.07001 0.236652 1.97 0.171524 1.85685C-0.200392 1.20935 0.0464098 0.452694 0.777102 0.106941C1.0976 -0.044504 1.39411 -0.0227873 1.69232 0.163519C2.07224 0.400116 2.42244 0.671003 2.7378 0.989324C3.5022 1.75855 4.2706 2.52321 5.03671 3.29015C5.1544 3.40788 5.2818 3.49246 5.45833 3.49931C5.652 3.50674 5.80568 3.44731 5.94165 3.30615C6.22558 3.01126 6.51866 2.72551 6.80831 2.43634C7.49501 1.74941 8.18286 1.06476 8.8667 0.374971C9.13007 0.109227 9.44257 -0.0193584 9.81392 0.00235831C9.91618 0.00864471 10.0219 0.026361 10.1178 0.0617934C10.7611 0.300105 11.2844 1.08648 10.8268 1.86599C10.7697 1.96257 10.692 2.04887 10.6126 2.12888C9.65224 3.09356 8.69246 4.05938 7.72524 5.01662C7.50929 5.23036 7.4396 5.45896 7.56128 5.73327C7.60184 5.82471 7.67326 5.90701 7.74467 5.97902C8.71074 6.95112 9.67852 7.92094 10.6469 8.89019Z"
                                        fill={`${
                                          isBitramp() ? "#EE33D9" : "#202A43"
                                        }`}
                                        fill-opacity={`${
                                          isBitramp() ? "" : "0.3"
                                        }`}
                                      />
                                    </svg>
                                  )}
                                  <span>KYC</span>
                                  <span className="ml-1 text-sm text-gray-500">
                                    last change{" "}
                                    {formatDate(
                                      userGeneralStatus?.data?.kyc_status
                                        ?.last_updated || ""
                                    )}
                                  </span>
                                </div>
                              </li>
                            </ul>
                            <div className="flex flex-col items-end gap-4">
                              <div className="flex items-center">
                                <label
                                  htmlFor={`fee-${client.id}`}
                                  className={`mr-2 ${
                                    isBitramp() ? "text-[#ffffff]" : ""
                                  }`}
                                >
                                  Fee
                                </label>
                                <div className="w-[8rem] flex gap-2 items-center">
                                  <input
                                    type="number"
                                    value={
                                      fee.txId === client.id.toString()
                                        ? fee.valueFee
                                        : Number(
                                            client.client_info?.fee ?? 0
                                          ).toFixed(2)
                                    }
                                    defaultValue={client.client_info?.fee}
                                    onChange={(e) =>
                                      setFee({
                                        txId: `${client.id}`,
                                        valueFee: Number(e.target.value),
                                      })
                                    }
                                    className="w-16 rounded-lg Input"
                                    disabled={!canEditClientsNEW}
                                  />
                                  <button
                                    onClick={handleSave}
                                    className={`btn-save ${
                                      isBitramp() ? "Primary_button" : ""
                                    }`}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                              <div
                                className={`text-right ${
                                  isBitramp() ? "text-[#ffffff]" : ""
                                }`}
                              >
                                User email: {client.email}
                              </div>
                              <div
                                className={`flex items-center" ${
                                  isBitramp() ? "text-[#ffffff]" : ""
                                }`}
                              >
                                <span>Assigned Salesperson ID</span>
                                <select
                                  className="ml-2 p-1 w-[8rem] border border-[#C0C0C0] rounded-md Input"
                                  disabled={!canEditClientsNEW}
                                >
                                  {/*!TODO Fix this*/}
                                  <option>ID 32432</option>
                                </select>
                              </div>
                            </div>
                          </div>
                          <textarea
                            className={`w-full mt-4 Input ${
                              declineReasonError
                                ? "border border-red-500"
                                : "border-none"
                            }`}
                            placeholder="Add reason"
                            onChange={(e) => {
                              setDeclineReason(e.target.value);
                              setDeclineReasonError(false); // resetăm eroarea când userul scrie
                            }}
                            value={
                              declineReason !==
                              userClientInfo?.data?.decline_reason
                                ? declineReason
                                : userClientInfo?.data?.decline_reason
                            }
                            disabled={!canEditClientsNEW}
                          />
                          {declineReasonError && (
                            <p className="text-red-500 text-xs mt-1">
                              This field is required for Freeze or Decline.
                            </p>
                          )}
                          <div className="flex items-center mt-4">
                            <div className="flex items-center gap-5">
                              <button
                                onClick={() => handleSettingsClick(client.id)}
                                className="flex items-center text-gray-600"
                                disabled={!canAccessClientsNEW}
                              >
                                <span className="mr-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="23"
                                    height="24"
                                    viewBox="0 0 23 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M11.9714 0C12.8574 0 13.6597 0.492107 14.1028 1.21855C14.3183 1.57006 14.462 2.00358 14.4261 2.46054C14.4021 2.81204 14.5099 3.16355 14.7015 3.49162C15.3121 4.48755 16.6652 4.86249 17.7189 4.30008C18.9044 3.62051 20.4011 4.03059 21.0837 5.19056L21.8859 6.57315C22.5804 7.73312 22.1973 9.22116 20.9998 9.88902C19.982 10.4866 19.6228 11.8106 20.2335 12.8182C20.4251 13.1346 20.6406 13.4041 20.9759 13.5681C21.395 13.7907 21.7183 14.1422 21.9458 14.4937C22.3888 15.2202 22.3529 16.1107 21.9219 16.8957L21.0837 18.3017C20.6406 19.0516 19.8144 19.5203 18.9642 19.5203C18.5452 19.5203 18.0782 19.4031 17.695 19.1688C17.3837 18.9696 17.0244 18.8993 16.6413 18.8993C15.4558 18.8993 14.462 19.8718 14.4261 21.0317C14.4261 22.3792 13.3244 23.4337 11.9474 23.4337H10.3189C8.92994 23.4337 7.82832 22.3792 7.82832 21.0317C7.80437 19.8718 6.81052 18.8993 5.62508 18.8993C5.22994 18.8993 4.87071 18.9696 4.57136 19.1688C4.18819 19.4031 3.70922 19.5203 3.3021 19.5203C2.43997 19.5203 1.61375 19.0516 1.17071 18.3017L0.344498 16.8957C-0.0985438 16.1341 -0.122492 15.2202 0.32055 14.4937C0.512136 14.1422 0.87136 13.7907 1.27848 13.5681C1.61375 13.4041 1.82929 13.1346 2.03285 12.8182C2.63155 11.8106 2.27233 10.4866 1.25453 9.88902C0.0690938 9.22116 -0.314078 7.73312 0.368447 6.57315L1.17071 5.19056C1.86521 4.03059 3.35 3.62051 4.54741 4.30008C5.58916 4.86249 6.94224 4.48755 7.55292 3.49162C7.7445 3.16355 7.85227 2.81204 7.82832 2.46054C7.80437 2.00358 7.93609 1.57006 8.1636 1.21855C8.60664 0.492107 9.4089 0.0234337 10.283 0H11.9714ZM11.1452 8.41269C9.26521 8.41269 7.7445 9.88902 7.7445 11.7286C7.7445 13.5681 9.26521 15.0327 11.1452 15.0327C13.0251 15.0327 14.5099 13.5681 14.5099 11.7286C14.5099 9.88902 13.0251 8.41269 11.1452 8.41269Z"
                                      fill="#9F9F9F"
                                    />
                                  </svg>
                                </span>
                                <div className="text-[#9F9F9F]">Settings</div>
                                {clientHasWarning ? (
                                  <svg
                                    width="18"
                                    height="16"
                                    viewBox="0 0 18 16"
                                    fill="none"
                                    className={"ml-2"}
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8.91901 0.00690456C9.25542 -0.0401983 9.60048 0.160234 9.80979 0.408996L17.9675 14.8654C18.0949 15.2501 17.8382 15.9647 17.3702 15.9998H0.604801C0.262459 15.9664 -0.0320001 15.3637 0.00280185 15.0568C0.0131684 14.9648 0.0615455 14.8713 0.104246 14.7903L8.23901 0.384709C8.35699 0.20979 8.71143 0.0360985 8.91901 0.00690456ZM8.74648 4.19611C8.34119 4.25205 7.76832 4.63108 7.71896 5.06138C7.69872 5.239 7.7392 5.41048 7.74364 5.57387C7.78831 7.26663 7.93616 8.94786 8.1114 10.6225C8.13584 10.8553 8.11486 11.0464 8.23087 11.2633C8.51669 11.7978 9.23568 11.8285 9.60246 11.3675C9.79868 11.121 9.75869 10.8224 9.78955 10.5246C9.95516 8.93093 10.1193 7.25485 10.1593 5.64943C10.1667 5.35038 10.2099 4.97331 10.0213 4.73117C9.72167 4.34601 9.23839 4.12865 8.74623 4.19636L8.74648 4.19611ZM8.86989 12.771C8.31108 12.8294 7.78683 13.2747 8.01909 13.8745C8.31034 14.6267 9.70958 14.6054 9.94924 13.8193C10.1489 13.1645 9.46646 12.7087 8.86989 12.771Z"
                                      fill="#FF0000"
                                    />
                                  </svg>
                                ) : null}
                              </button>
                              <button
                                onClick={() =>
                                  handleTransactionClick(client.id)
                                }
                                className={`flex items-center text-[#111B31] ${
                                  isBitramp() ? "text-[#ffffff]" : ""
                                }`}
                                disabled={!canAccessTransactionsNEW}
                              >
                                <span className="mr-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M15.7592 2.08595C15.7123 2.37887 15.6889 2.67179 15.6889 2.96471C15.6889 5.601 17.8213 7.7323 20.4459 7.7323C20.7388 7.7323 21.02 7.69832 21.3129 7.65145V17.1058C21.3129 21.079 18.9696 23.434 14.9858 23.434H6.32827C2.34337 23.434 0 21.079 0 17.1058V8.43648C0 4.45275 2.34337 2.08595 6.32827 2.08595H15.7592ZM15.9947 9.20979C15.6771 9.17464 15.362 9.31524 15.1733 9.57301L12.339 13.2404L9.09227 10.6861C8.89308 10.5338 8.65875 10.474 8.42441 10.4986C8.19124 10.5338 7.98034 10.6615 7.83857 10.849L4.37155 15.3611L4.30008 15.4666C4.10089 15.8404 4.19463 16.3207 4.54613 16.5797C4.71017 16.6851 4.88592 16.7554 5.08511 16.7554C5.35577 16.7672 5.61237 16.6254 5.7764 16.4039L8.71733 12.6182L12.0566 15.1268L12.1621 15.1959C12.537 15.3951 13.0057 15.3025 13.2752 14.9499L16.6613 10.5807L16.6145 10.6041C16.802 10.3463 16.8371 10.0183 16.7082 9.72533C16.5805 9.43241 16.2981 9.23322 15.9947 9.20979ZM20.61 0C22.1684 0 23.4338 1.26542 23.4338 2.82376C23.4338 4.3821 22.1684 5.64752 20.61 5.64752C19.0517 5.64752 17.7863 4.3821 17.7863 2.82376C17.7863 1.26542 19.0517 0 20.61 0Z"
                                      fill={` ${
                                        isBitramp() ? "#EE33D9" : "#111B31"
                                      }`}
                                    />
                                  </svg>
                                </span>{" "}
                                Transactions
                              </button>
                            </div>
                            <div className="flex items-center gap-2 ml-[29rem]">
                              <button
                                onClick={() => {
                                  setClientIdForConfirm(client.id);
                                  setConfirmModal(true);
                                  setConfirmStatusType("approve");
                                }}
                                className={`Approve_button ${
                                  isBitramp()
                                    ? "text-[#2dde248d] border-none hover:text-[#2dde24] hover:bg-transparent"
                                    : ""
                                }`}
                                disabled={!canEditClientsNEW}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  if (!declineReason.trim()) {
                                    setDeclineReasonError(true);
                                    return;
                                  }

                                  setClientIdForConfirm(client.id);
                                  setConfirmModal(true);
                                  setConfirmStatusType("freeze");
                                }}
                                className={`Freeze_button ${
                                  isBitramp()
                                    ? "text-[#07baeb9e] hover:text-[#07baeb] border-none hover:bg-transparent"
                                    : ""
                                }`}
                                disabled={!canEditClientsNEW}
                              >
                                Freeze
                              </button>
                              <button
                                onClick={() => {
                                  if (!declineReason.trim()) {
                                    setDeclineReasonError(true);
                                    return;
                                  }

                                  setClientIdForConfirm(client.id);
                                  setConfirmModal(true);
                                  setConfirmStatusType("decline");
                                }}
                                className={`Outline_red_button ${
                                  isBitramp()
                                    ? "!text-[#ff000094] hover:!text-[#ff0000] !border-none !bg-transparent hover:!bg-transparent"
                                    : ""
                                }`}
                                disabled={!canEditClientsNEW}
                              >
                                Decline
                              </button>
                              <button
                                onClick={() => {
                                  setClientIdForConfirm(client.id);
                                  setConfirmModal(true);
                                  setConfirmStatusType("pending");
                                }}
                                className={`Pending_button" ${
                                  isBitramp()
                                    ? "text-[#c3c3c383] hover:text-[#c3c3c3] border-none hover:bg-transparent"
                                    : ""
                                }`}
                                disabled={!canEditClientsNEW}
                              >
                                Pending
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <ClientSettings client_settings_id={clientSettings} />
      )}

      {confirmModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <div
            className={`${
              isBitramp() ? "bg-[#2B2B2B] border-[#212121]" : "bg-white"
            } w-[30rem] h-auto rounded-[1.5rem] border text-center relative`}
          >
            <button
              className="absolute top-4 right-4"
              onClick={() => setConfirmModal(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <path
                  d="M17 17L2 2"
                  stroke={`${isBitramp() ? "#fff" : "#202A43"}`}
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <path
                  d="M2 17L17 2"
                  stroke={`${isBitramp() ? "#fff" : "#202A43"}`}
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <div
              className={`${
                isBitramp() ? "text-white" : "text-[#05004E]"
              } pt-[2.5rem] text-center text-[1.5rem] font-[700] font-satoshi mt-[2rem] px-[2rem]`}
            >
              Please make sure your data and action are correct before
              proceeding
            </div>
            <button
              onClick={() => {
                if (confirtStatusType === "approve") {
                  handleApprove(clinetIdForConfirm);
                } else if (confirtStatusType === "freeze") {
                  handleFreeze(clinetIdForConfirm);
                } else if (confirtStatusType === "decline") {
                  handleDecline(clinetIdForConfirm);
                } else if (confirtStatusType === "pending") {
                  handlePending(clinetIdForConfirm);
                }
                setConfirmModal(false);
              }}
              className={`${
                isBitramp() ? "Primary_button ml-[11rem]" : ""
              } bg-primary items-center text-white p-4 px-7 rounded-lg text-[1.1875rem] font-satoshi mt-[4rem]`}
            >
              Confirm
            </button>
            <div
              className={`${
                isBitramp() ? "text-[#9F9F9F]" : "text-[#737791]"
              } text-[1.2rem] px-[3rem] pt-[1.5rem] font-[500] text-center font-satoshi mb-[2rem]`}
            >
              Once confirmed, your action will be processed and cannot be
              undone.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
